// Data hook for the public, read-only tournament view. Loads a shared snapshot
// once, then keeps it fresh by polling every 15s and refetching whenever the
// tab regains focus — so a spectator watching on their phone sees scores update
// without reloading. A background refresh keeps the last good snapshot on
// transient failure; only the very first load surfaces a hard error.

import { useCallback, useEffect, useRef, useState } from 'react';
import type { TournamentState } from '../../domain';
import { fetchPublicTournament } from '../../data/publicClient';

export type PublicLoadStatus = 'loading' | 'ready' | 'notfound' | 'error';

export interface PublicTournamentResult {
  tournament: TournamentState | null;
  status: PublicLoadStatus;
  error: string | null;
  /** Epoch ms of the last successful load, or null before the first one. */
  lastUpdated: number | null;
  /** True while a background refresh is in flight. */
  refreshing: boolean;
  refresh: () => void;
}

const POLL_INTERVAL_MS = 15_000;

export function usePublicTournament(id: string): PublicTournamentResult {
  const [tournament, setTournament] = useState<TournamentState | null>(null);
  const [status, setStatus] = useState<PublicLoadStatus>('loading');
  const [error, setError] = useState<string | null>(null);
  const [lastUpdated, setLastUpdated] = useState<number | null>(null);
  const [refreshing, setRefreshing] = useState(false);

  const mounted = useRef(true);
  const inFlight = useRef(false);

  const load = useCallback(
    async (mode: 'initial' | 'background') => {
      if (inFlight.current) return; // never overlap requests
      inFlight.current = true;
      if (mode === 'background') setRefreshing(true);
      try {
        const next = await fetchPublicTournament(id);
        if (!mounted.current) return;
        if (next === null) {
          setTournament(null);
          setStatus('notfound');
        } else {
          setTournament(next);
          setStatus('ready');
          setError(null);
          setLastUpdated(Date.now());
        }
      } catch (e: unknown) {
        if (!mounted.current) return;
        setError(e instanceof Error ? e.message : 'Failed to load tournament.');
        // Keep showing the last good data on a background failure; only the
        // first load drops to a hard error state.
        setStatus((prev) => (prev === 'ready' ? 'ready' : 'error'));
      } finally {
        if (mounted.current) setRefreshing(false);
        inFlight.current = false;
      }
    },
    [id],
  );

  const refresh = useCallback(() => void load('background'), [load]);

  useEffect(() => {
    mounted.current = true;
    void load('initial');

    const interval = window.setInterval(() => {
      if (document.visibilityState === 'visible') void load('background');
    }, POLL_INTERVAL_MS);

    const onVisible = () => {
      if (document.visibilityState === 'visible') void load('background');
    };
    document.addEventListener('visibilitychange', onVisible);

    return () => {
      mounted.current = false;
      window.clearInterval(interval);
      document.removeEventListener('visibilitychange', onVisible);
    };
  }, [load]);

  return { tournament, status, error, lastUpdated, refreshing, refresh };
}
